import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { getAddedDishes } from './salesNameCheckApi';
import { formatNumber, priceText, showUnderReviewNote } from './sncShared';

/**
 * Shown when the check is done or skipped (spec §12 "Continue screen", §10.1):
 * the dishes Add new created, each with Open in Item Mapping, then Continue to
 * Ingredient Reconciliation. Reopened from Settings it offers Run again and
 * Done (back to Settings) instead.
 */
const summaryParts = (s) => {
  if (!s) return [];
  const parts = [];
  if (s.saved) parts.push(`${formatNumber(s.saved)} name${s.saved === 1 ? '' : 's'} saved`);
  if (s.priceDecides) parts.push(`${formatNumber(s.priceDecides)} matched by price`);
  if (s.added) parts.push(`${formatNumber(s.added)} dish${s.added === 1 ? '' : 'es'} added`);
  if (s.leftForNim) parts.push(`${formatNumber(s.leftForNim)} left for Not in Menu`);
  return parts;
};

export default function ContinueScreen({
  mode, checkState, summary, busy, onContinue, onDone, onRunAgain, onOpenItemMap,
}) {
  const [dishes, setDishes] = useState(null);
  const [loadError, setLoadError] = useState('');
  // A ref, not state: two clicks in one tick both see the old state.
  const continuingRef = useRef(false);

  useEffect(() => {
    let alive = true;
    getAddedDishes().then((r) => {
      if (!alive) return;
      if (r.ok) {
        setDishes(Array.isArray(r.dishes) ? r.dishes : []);
      } else {
        setDishes([]);
        setLoadError(r.error);
      }
    });
    return () => { alive = false; };
  }, []);

  const isReopen = mode === 'reopen';
  const skipped = checkState === 'skipped';
  const parts = summaryParts(summary);

  const handleContinue = async () => {
    if (continuingRef.current || busy || !onContinue) return;
    continuingRef.current = true;
    try {
      await onContinue();
    } finally {
      continuingRef.current = false;
    }
  };

  return (
    <section className="snc-card">
      <div className="snc-card__title-row">
        {!skipped && <CheckCircle2 size={16} className="snc-done-icon" />}
        <h2>{skipped ? 'Sales Name Check was skipped' : 'Sales Name Check is done'}</h2>
      </div>
      <p className="snc-card__subtitle">
        {skipped
          ? 'Nothing new was remembered. Names the POS prints differently will come up in Not in Menu.'
          : (parts.length > 0 ? `${parts.join(' · ')}.` : 'Every name is matched, linked, added or skipped.')}
      </p>

      {dishes === null && <p className="snc-loading">Loading added dishes…</p>}
      {loadError && <p className="snc-error">{loadError}</p>}
      {dishes && dishes.length > 0 && (
        <>
          {showUnderReviewNote(dishes) && (
            <p className="snc-note snc-note--warn">
              These dishes were added from your sales and stay Under Review (orange) until their recipes are confirmed
              in Item Mapping. Their ingredients will be checked in Ingredient Reconciliation once confirmed.
            </p>
          )}
          <div className="snc-added">
            {dishes.map((dish) => (
              <div key={dish.menuItemId} className="snc-added__row">
                <div className="snc-added__main">
                  <span className="snc-added__name">{dish.name}</span>
                  <span className="snc-added__meta">
                    {priceText(dish.price)} · sold as “{dish.posName}” · {formatNumber(dish.plates)} plates
                  </span>
                </div>
                <span className={dish.recipeStatus === 'confirmed' ? 'snc-status-confirmed' : 'snc-status-review'}>
                  {dish.recipeStatus === 'confirmed' ? 'Confirmed' : 'Under Review'}
                </span>
                <button
                  type="button"
                  className="snc-action"
                  onClick={() => onOpenItemMap && onOpenItemMap(dish.menuItemId)}
                >
                  Open in Item Mapping
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="snc-footer">
        {isReopen ? (
          <button type="button" className="snc-btn snc-btn--outline" onClick={onRunAgain} disabled={busy}>
            Run again
          </button>
        ) : <span />}
        {isReopen ? (
          <button type="button" className="snc-btn snc-btn--primary" onClick={onDone} disabled={busy}>
            Done
          </button>
        ) : (
          <button type="button" className="snc-btn snc-btn--primary" onClick={handleContinue} disabled={busy}>
            Continue to Ingredient Reconciliation
          </button>
        )}
      </div>
    </section>
  );
}
